import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Clock, DollarSign, FileText, Lock, Plus, Trash2 } from 'lucide-react';
import Modal from '../../Modal';
import CreateTaskModal from '../task/CreateTaskModal';
import ProjectMailSettingsModal from './ProjectMailSettingsModal';
import { fullLogoUrl } from '../ReportPdfButton';
import { buildProjectReportPdf } from './ProjectReportPdf';
import { useApi } from '../../../lib/api';
import { useWorkspace } from '../../../lib/workspace';
import { formatHoursMinutes } from '../../../lib/format';

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TODAY   = new Date().toISOString().substring(0, 10);
const MAX_ENTRY_AMOUNT = 999999999999;

const TASK_STATUS_META = {
  open:        { label: 'Open',        color: '#38bdf8', bg: 'rgba(56,189,248,0.12)',  border: '#38bdf8' },
  in_progress: { label: 'In Progress', color: '#fbbf24', bg: 'rgba(251,191,36,0.12)',  border: '#fbbf24' },
  blocked:     { label: 'Blocked',     color: '#f87171', bg: 'rgba(248,113,113,0.12)', border: '#f87171' },
  done:        { label: 'Done',        color: '#4ad295', bg: 'rgba(74,210,149,0.12)',  border: '#4ad295' },
  cancelled:   { label: 'Cancelled',   color: '#94a3b8', bg: 'rgba(148,163,184,0.1)',  border: '#94a3b8' },
};

const PROJECT_STATUS_META = {
  active:    { label: 'Active',    color: '#38bdf8' },
  completed: { label: 'Completed', color: '#4ade80' },
  overdue:   { label: 'Overdue',   color: '#f87171' },
  near_end:  { label: 'Near End',  color: '#fbbf24' },
  inactive:  { label: 'Inactive',  color: '#94a3b8' },
  deleted:   { label: 'Deleted',   color: '#f87171' },
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function SettingsMenuItem({ label, onClick, disabled }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        padding: '8px 10px', background: 'none', border: 'none', textAlign: 'left',
        fontSize: 13, color: 'var(--text-primary)', cursor: disabled ? 'default' : 'pointer',
        borderRadius: 6, opacity: disabled ? 0.6 : 1,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = 'var(--bg-3, rgba(0,0,0,0.05))'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
    >
      {label}
    </button>
  );
}

// Settings icon button that opens the project's action menu: the per-project
// actions passed in from the listing page (Update, Notify, Edit, Feed, Set
// Inactive…) followed by Mail Control and Download PDF.
function SettingsMenu({ actions = [], onSelectMailControl, onSelectDownload, showDownload, downloadBusy }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    function onDocClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Project settings"
        aria-label="Project settings"
        style={{
          background: 'none', border: 'none', padding: 4, lineHeight: 1,
          color: 'var(--text-muted)', cursor: 'pointer', display: 'flex',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.color = 'var(--text-primary)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--text-muted)'; }}
      >
        <svg viewBox="0 0 24 24" width="19" height="19" fill="currentColor">
          <path d="M19.14,12.94c0.04-0.3,0.06-0.61,0.06-0.94c0-0.32-0.02-0.64-0.07-0.94l2.03-1.58c0.18-0.14,0.23-0.41,0.12-0.61 l-1.92-3.32c-0.12-0.22-0.37-0.29-0.59-0.22l-2.39,0.96c-0.5-0.38-1.03-0.7-1.62-0.94L14.4,2.81c-0.04-0.24-0.24-0.41-0.48-0.41 h-3.84c-0.24,0-0.43,0.17-0.47,0.41L9.25,5.35C8.66,5.59,8.12,5.92,7.63,6.29L5.24,5.33c-0.22-0.08-0.47,0-0.59,0.22L2.74,8.87 C2.62,9.08,2.66,9.34,2.86,9.48l2.03,1.58C4.84,11.36,4.8,11.69,4.8,12s0.02,0.64,0.07,0.94l-2.03,1.58 c-0.18,0.14-0.23,0.41-0.12,0.61l1.92,3.32c0.12,0.22,0.37,0.29,0.59,0.22l2.39-0.96c0.5,0.38,1.03,0.7,1.62,0.94l0.36,2.54 c0.05,0.24,0.24,0.41,0.48,0.41h3.84c0.24,0,0.44-0.17,0.47-0.41l0.36-2.54c0.59-0.24,1.13-0.56,1.62-0.94l2.39,0.96 c0.22,0.08,0.47,0,0.59-0.22l1.92-3.32c0.12-0.22,0.07-0.47-0.12-0.61L19.14,12.94z M12,15.6c-1.98,0-3.6-1.62-3.6-3.6 s1.62-3.6,3.6-3.6s3.6,1.62,3.6,3.6S13.98,15.6,12,15.6z" />
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6, zIndex: 20,
          background: 'var(--bg-card, #fff)', border: '1px solid var(--border)', borderRadius: 8,
          boxShadow: '0 6px 20px rgba(0,0,0,0.15)', minWidth: 170, padding: 4,
        }}>
          {actions.map((a) => (
            <SettingsMenuItem
              key={a.label}
              label={a.label}
              disabled={a.disabled}
              onClick={() => { if (!a.disabled) { setOpen(false); a.onClick(); } }}
            />
          ))}
          <SettingsMenuItem
            label="✉️ Mail Control"
            onClick={() => { setOpen(false); onSelectMailControl(); }}
          />
          {showDownload && (
            <SettingsMenuItem
              label={downloadBusy ? '⏳ Preparing PDF…' : '⬇️ Download PDF'}
              disabled={downloadBusy}
              onClick={() => { if (!downloadBusy) { setOpen(false); onSelectDownload(); } }}
            />
          )}
        </div>
      )}
    </div>
  );
}

function SectionHeader({ title, action }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
      <span className="tdv2-section-label" style={{ marginBottom: 0 }}>{title}</span>
      {action}
    </div>
  );
}

function MilestoneForm({ onSave, onCancel, initial }) {
  const [title,     setTitle]     = useState(initial?.title      || '');
  const [startDate, setStartDate] = useState(initial?.start_date || '');
  const [endDate,   setEndDate]   = useState(initial?.end_date   || '');
  const [error,     setError]     = useState(null);
  const [saving,    setSaving]    = useState(false);

  async function handleSave() {
    if (!title.trim()) { setError('Title is required'); return; }
    if (startDate && !DATE_RE.test(startDate)) { setError('Invalid start date'); return; }
    if (endDate   && !DATE_RE.test(endDate))   { setError('Invalid end date');   return; }
    if (startDate && endDate && endDate < startDate) { setError('End date must be on or after start date'); return; }
    setSaving(true);
    try {
      await onSave({ title: title.trim(), start_date: startDate || null, end_date: endDate || null });
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to save');
      setSaving(false);
    }
  }

  return (
    <div style={{
      background: 'rgba(6,182,212,0.06)', border: '1px solid rgba(6,182,212,0.2)',
      borderRadius: 10, padding: '14px 16px',
      display: 'flex', flexDirection: 'column', gap: 10,
    }}>
      <input
        type="text" className="form-input" placeholder="Milestone title *"
        value={title} onChange={(e) => setTitle(e.target.value)}
        maxLength={200} autoFocus style={{ margin: 0 }}
      />
      <div style={{ display: 'flex', gap: 10 }}>
        <input type="date" className="form-input" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={{ margin: 0, flex: 1 }} />
        <input type="date" className="form-input" value={endDate}   onChange={(e) => setEndDate(e.target.value)}   style={{ margin: 0, flex: 1 }} />
      </div>
      {error && <div className="form-error" style={{ margin: 0 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
        <button className="btn-secondary" style={{ padding: '4px 14px', fontSize: 13 }} onClick={onCancel} disabled={saving}>Cancel</button>
        <button className="btn-primary"   style={{ padding: '4px 14px', fontSize: 13 }} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : (initial ? 'Save' : 'Add milestone')}
        </button>
      </div>
    </div>
  );
}

// ── Financials Panel (sidebar, admin only) ──────────────────────────────────
// Estimated hours/cost always come from each assignee's task estimations;
// actual hours/cost always come from logged task time — regardless of billing type.

function money(n) {
  const v = Number(n) || 0;
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

// Same Indian lakh/crore convention as the project list's formatValue() —
// used only for the PDF's summary cards, where shorter values keep every
// card on one line instead of wrapping.
function moneyShort(n) {
  const v = Number(n) || 0;
  const abs = Math.abs(v);
  if (abs >= 1e7) return `${(v / 1e7).toLocaleString('en-IN', { maximumFractionDigits: 2 })}Cr`;
  if (abs >= 1e5) return `${(v / 1e5).toLocaleString('en-IN', { maximumFractionDigits: 2 })}L`;
  return money(v);
}

// Financials figures store hours as a decimal (e.g. 2.02); render as "2h 1m".
function hoursLabel(hoursDecimal) {
  const h = Number(hoursDecimal) || 0;
  return formatHoursMinutes(Math.round(h * 60));
}

// Ledger entry_date comes back as an ISO timestamp (e.g. 2026-07-14T00:00:00.000Z);
// only the date part is meaningful here.
function dateOnly(d) {
  if (!d) return '';
  return String(d).slice(0, 10);
}

// "2026-09-16" -> "16 Sept 2026"
function ledgerDate(isoDate) {
  if (!isoDate) return '';
  return new Date(`${isoDate}T00:00`).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
}

const Dash = () => <span style={{ color: 'var(--fg-mute)' }}>—</span>;

// Financial grid accent hues — match the approved reference design exactly.
const FIN_BLUE  = '#2563eb';
const FIN_RED   = '#d92d2d';
const FIN_GREEN = '#12805c';

// Bordered-grid cell shared by the unified Financial grid. Header cells pass
// tight=true for narrower horizontal padding, since short uppercase labels
// (SL NO, ACTION) need less room than the data rows below them.
function cellStyle(align, borderRight, tight) {
  return {
    padding: tight ? '10px 6px' : '13px 12px', minWidth: 0, textAlign: align || 'left',
    borderRight: borderRight ? '1px solid var(--border)' : 'none',
    fontVariantNumeric: align === 'right' ? 'tabular-nums' : undefined,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };
}

function ProgressBar({ pct }) {
  const clamped = Math.min(100, Math.max(0, pct || 0));
  return (
    <div style={{ height: 4, borderRadius: 2, background: 'var(--bg-muted)', overflow: 'hidden', marginTop: 10 }}>
      <div style={{ height: '100%', borderRadius: 2, background: FIN_BLUE, minWidth: 3, width: `${clamped}%`, transition: 'width .3s ease' }} />
    </div>
  );
}

function StatTile({ label, value, valueColor, sub, progress }) {
  return (
    <div style={{
      minWidth: 0,
      background: 'var(--fin-card-bg)', border: '1px solid var(--border)',
      borderRadius: 12, padding: '16px 18px',
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--fg-mute)' }}>
        {label}
      </div>
      <div
        style={{
          fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 22, fontWeight: 700,
          letterSpacing: '-0.01em', color: valueColor || 'var(--fg)', marginTop: 6,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}
        title={value}
      >
        {value}
      </div>
      {progress != null && <ProgressBar pct={progress} />}
      {sub && <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--fg-dim)', marginTop: progress != null ? 6 : 4 }}>{sub}</div>}
    </div>
  );
}

// Type badge for a Financial grid row: Invoice (blue), Cost (red), or the
// locked Auto-tracked labor row (neutral).
function typeBadge(kind, auto) {
  if (auto) return { label: 'AUTO', color: 'var(--fg-mute)', bg: 'var(--bg-muted)' };
  if (kind === 'invoice') return { label: 'INVOICE', color: FIN_BLUE, bg: 'rgba(37,99,235,0.12)' };
  return { label: 'COST', color: FIN_RED, bg: 'rgba(217,45,45,0.12)' };
}

function FinancialsPanel({ appId, api, projectId, startDate, readOnly }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const [addEntryOpen, setAddEntryOpen] = useState(false);
  const [kind,        setKind]        = useState('cost');
  const [description, setDescription] = useState('');
  const [amount,       setAmount]     = useState('');
  const [entryDate,    setEntryDate]  = useState(TODAY);
  const [submitting,   setSubmitting] = useState(false);
  const [formError,    setFormError]  = useState(null);
  const descInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.getProjectFinancials(appId, projectId);
      setData(res);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load financials');
    } finally {
      setLoading(false);
    }
  }, [api, appId, projectId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!addEntryOpen) return;
    const t = setTimeout(() => descInputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, [addEntryOpen]);

  function openAddEntry(preferredKind) {
    setFormError(null);
    setKind(preferredKind || 'cost');
    setDescription(''); setAmount(''); setEntryDate(TODAY);
    setAddEntryOpen(true);
  }

  async function handleAddEntry(e) {
    e.preventDefault();
    setFormError(null);
    if (!description.trim()) { setFormError('Description is required'); return; }
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) { setFormError('Amount must be a positive number'); return; }
    if (amt > MAX_ENTRY_AMOUNT) { setFormError(`Amount too large (max ${MAX_ENTRY_AMOUNT.toLocaleString()})`); return; }
    setSubmitting(true);
    try {
      await api.createProjectCost(appId, projectId, {
        kind, description: description.trim(), amount: amt, entry_date: entryDate || null,
      });
      setAddEntryOpen(false);
      setDescription(''); setAmount(''); setEntryDate(TODAY);
      await load();
    } catch (err) {
      setFormError(err.response?.data?.error || err.message || 'Failed to add entry');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleDeleteEntry(cid) {
    try {
      await api.deleteProjectCost(appId, projectId, cid);
      await load();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    }
  }

  if (loading) return <div className="tl-loading"><span className="tl-spinner" /> Loading financials…</div>;
  if (error)   return <div className="banner banner-error">{error}</div>;
  if (!data)   return null;

  const costEntries    = (data.costs || []).filter((c) => c.kind !== 'invoice');
  const invoiceEntries = (data.costs || []).filter((c) => c.kind === 'invoice');
  const hasLabor = Number(data.actual_labor_cost) > 0;

  // Unified ledger: the auto-computed labor cost row (not deletable, shown
  // under today's date since it isn't backed by a real ledger date) plus
  // every manual cost/invoice entry, so Costs and Invoices read as one grid.
  const allEntryRows = [];
  if (hasLabor) {
    allEntryRows.push({
      id: 'labor-auto', type: 'cost', auto: true,
      description: `Hours worked${data.actual_hours ? ` (${hoursLabel(data.actual_hours)})` : ''}`,
      dateISO: TODAY, actual: null, cost: Number(data.actual_labor_cost) || 0,
    });
  }
  allEntryRows.push(...costEntries.map((c) => ({
    id: c.id, type: 'cost', auto: false, description: c.description, dateISO: dateOnly(c.entry_date),
    actual: null, cost: Number(c.amount) || 0,
  })));
  allEntryRows.push(...invoiceEntries.map((c) => ({
    id: c.id, type: 'invoice', auto: false, description: c.description, dateISO: dateOnly(c.entry_date),
    actual: Number(c.amount) || 0, cost: null,
  })));
  allEntryRows.sort((a, b) => b.dateISO.localeCompare(a.dateISO));

  const revenue       = Number(data.revenue) || 0;
  const invoicedTotal = Number(data.invoiced_total) || 0;
  const totalCost     = Number(data.total_cost) || 0;
  const yetToInvoice  = Math.max(0, revenue - invoicedTotal);
  const billedPct     = revenue > 0 ? (invoicedTotal / revenue) * 100 : 0;
  const profitToDate  = invoicedTotal - totalCost;
  const profit        = Number(data.profit) || 0;

  const cardStyle = {
    background: 'var(--fin-card-bg)', border: '1px solid var(--border)', borderRadius: 12,
    padding: 0, overflow: 'hidden',
  };
  const gridCols = '52px minmax(180px,2fr) 100px 108px 100px 108px 100px 68px';
  const headRowStyle = {
    display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', borderTop: '1px solid var(--border)',
    background: 'var(--bg-muted)', fontSize: 11, fontWeight: 700, color: 'var(--fg-mute)', letterSpacing: '.02em',
  };
  const rowStyle = { display: 'grid', gridTemplateColumns: gridCols, alignItems: 'center', borderTop: '1px solid var(--border)', fontSize: 13 };
  const totalRowStyle = { ...rowStyle, borderTop: '1px solid var(--border-strong)', background: 'var(--bg-muted)', fontWeight: 700 };
  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg-mute)', marginBottom: 6 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Top stat strip — five equal KPI tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 14 }}>
        <StatTile
          label="Project value"
          value={money(revenue)}
          sub={data.billing_type === 'per_hour' ? 'Estimated revenue, per hour' : 'Estimated revenue, fixed rate'}
        />
        <StatTile
          label="Invoiced"
          value={money(invoicedTotal)}
          valueColor={FIN_BLUE}
          progress={billedPct}
          sub={`${Math.round(billedPct)}% billed, ${money(yetToInvoice)} yet to invoice`}
        />
        <StatTile
          label="Total cost"
          value={money(totalCost)}
          valueColor={FIN_RED}
          sub={`Labour ${money(data.actual_labor_cost)}, manual ${money(data.other_costs_total)}`}
        />
        <StatTile
          label="Projected profit"
          value={money(profit)}
          valueColor={profit >= 0 ? FIN_GREEN : FIN_RED}
          sub={data.margin_pct != null ? `${data.margin_pct}% margin` : '—'}
        />
        <StatTile
          label="Profit to date"
          value={money(profitToDate)}
          valueColor={profitToDate >= 0 ? FIN_GREEN : FIN_RED}
          sub="Invoiced minus cost"
        />
      </div>

      {/* Financial grid — Revenue (the estimate) at the top, every invoice/cost
          entry below it, then a Total row and the Profit/Loss banner. One
          table so the whole financial story reads top to bottom. */}
      <div style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 10 }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 999,
            fontSize: 11, fontWeight: 700, letterSpacing: '.02em', color: 'var(--fg-mute)', background: 'var(--bg-muted)',
          }}>
            {data.billing_type === 'per_hour' ? 'PER HOUR' : 'FIXED RATE'}
          </span>
          {!readOnly && (
          <button
            type="button"
            onClick={() => openAddEntry('cost')}
            style={{
              font: 'inherit', display: 'flex', alignItems: 'center', gap: 6, height: 34,
              padding: '0 14px', fontSize: 13, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
              border: '1px solid var(--border-strong)', background: 'var(--fin-card-bg)', color: 'var(--fg)',
            }}
          >
            <Plus size={14} /> Add entry
          </button>
          )}
        </div>

        {data.billing_type === 'per_hour' && (
          <div style={{ fontSize: 12, color: 'var(--fg-dim)', padding: '10px 20px 0' }}>
            {data.total_estimated_hours ? (
              <><strong style={{ color: 'var(--fg)' }}>{money(data.total_billing)}</strong>/hr × <strong style={{ color: 'var(--fg)' }}>{hoursLabel(data.total_estimated_hours)}</strong></>
            ) : (
              <><strong style={{ color: 'var(--fg)' }}>{money(data.total_billing)}</strong>/hr, no total estimated hours set yet</>
            )}
          </div>
        )}

        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 640 }}>
            <div style={headRowStyle}>
              <div style={cellStyle('left', true, true)}>SL NO</div>
              <div style={cellStyle('left', true, true)}>DESCRIPTION</div>
              <div style={cellStyle('left', true, true)}>TYPE</div>
              <div style={cellStyle('left', true, true)}>DATE</div>
              <div style={cellStyle('right', true, true)}>ESTIMATE</div>
              <div style={cellStyle('right', true, true)}>ACTUAL (AMT)</div>
              <div style={cellStyle('right', true, true)}>COST</div>
              <div style={cellStyle('center', false, true)}>ACTION</div>
            </div>

            {/* Project value — the system row, always first, never deletable.
                Dated to the project's own start date, not a ledger entry. */}
            <div style={rowStyle}>
              <div style={{ ...cellStyle('left', true), fontWeight: 600, color: 'var(--fg)' }}>1</div>
              <div style={{ ...cellStyle('left', true), fontWeight: 700, color: 'var(--fg)' }}>Project value</div>
              <div style={cellStyle('left', true)}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 999,
                  fontSize: 11, fontWeight: 700, letterSpacing: '.01em', whiteSpace: 'nowrap',
                  color: 'var(--fg-mute)', background: 'var(--bg-muted)',
                }}>
                  REVENUE
                </span>
              </div>
              <div style={{ ...cellStyle('left', true), color: 'var(--fg-dim)' }}>{startDate ? ledgerDate(dateOnly(startDate)) : <Dash />}</div>
              <div style={{ ...cellStyle('right', true), fontWeight: 700, color: 'var(--fg)' }}>{revenue ? money(revenue) : <Dash />}</div>
              <div style={{ ...cellStyle('right', true), color: 'var(--fg-mute)' }}><Dash /></div>
              <div style={{ ...cellStyle('right', true), color: 'var(--fg-mute)' }}><Dash /></div>
              <div style={{ display: 'flex', justifyContent: 'center' }}>
                <span title="System row, locked" style={{ color: 'var(--fg-mute)', cursor: 'help', display: 'flex' }}>
                  <Lock size={14} />
                </span>
              </div>
            </div>

            {allEntryRows.length === 0 ? (
              <div style={{ borderTop: '1px solid var(--border)', padding: '20px 0', textAlign: 'center', color: 'var(--fg-dim)', fontSize: 13 }}>
                No invoices or costs yet. Add one above.
              </div>
            ) : (
              allEntryRows.map((r, i) => {
                const badge = typeBadge(r.type, r.auto);
                return (
                  <div
                    key={r.id} style={rowStyle}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-muted)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{ ...cellStyle('left', true), fontWeight: 600, color: 'var(--fg)' }}>{i + 2}</div>
                    <div style={{ ...cellStyle('left', true), display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, color: 'var(--fg)' }}>
                      {r.auto && <Clock size={13} style={{ flexShrink: 0, color: 'var(--fg-mute)' }} />}
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.description}>{r.description}</span>
                    </div>
                    <div style={cellStyle('left', true)}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', height: 20, padding: '0 8px', borderRadius: 999,
                        fontSize: 11, fontWeight: 700, letterSpacing: '.01em', whiteSpace: 'nowrap',
                        color: badge.color, background: badge.bg,
                      }}>
                        {badge.label}
                      </span>
                    </div>
                    <div style={{ ...cellStyle('left', true), color: 'var(--fg-dim)' }}>{ledgerDate(r.dateISO)}</div>
                    <div style={{ ...cellStyle('right', true), color: 'var(--fg-mute)' }}><Dash /></div>
                    <div style={{ ...cellStyle('right', true), color: FIN_BLUE, fontWeight: 700 }}>{r.actual != null ? money(r.actual) : <Dash />}</div>
                    <div style={{ ...cellStyle('right', true), color: FIN_RED, fontWeight: 700 }}>{r.cost != null ? money(r.cost) : <Dash />}</div>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {r.auto ? (
                        <span title="Auto-calculated from logged task hours. Edit hours in Tasks." style={{ color: 'var(--fg-mute)', cursor: 'help', display: 'flex' }}>
                          <Lock size={14} />
                        </span>
                      ) : readOnly ? null : (
                        <button
                          onClick={() => handleDeleteEntry(r.id)}
                          title="Delete entry"
                          style={{ font: 'inherit', background: 'none', border: 'none', color: 'var(--fg-mute)', cursor: 'pointer', padding: 4, display: 'flex' }}
                          onMouseEnter={(e) => { e.currentTarget.style.color = FIN_RED; }}
                          onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--fg-mute)'; }}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })
            )}

            <div style={totalRowStyle}>
              <div style={cellStyle('left', true)} />
              <div style={{ ...cellStyle('left', true), color: 'var(--fg)' }}>Total</div>
              <div style={cellStyle('left', true)} />
              <div style={cellStyle('left', true)} />
              <div style={{ ...cellStyle('right', true), color: 'var(--fg)' }}>{money(revenue)}</div>
              <div style={{ ...cellStyle('right', true), color: FIN_BLUE }}>{money(invoicedTotal)}</div>
              <div style={{ ...cellStyle('right', true), color: FIN_RED }}>{money(totalCost)}</div>
              <div style={cellStyle('left', false)} />
            </div>
          </div>
        </div>

        {/* Profit/Loss = Revenue - Cost (projected), meaningful once billing is set. */}
        {revenue > 0 ? (
          <div style={{
            padding: '16px 20px',
            display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
            background: profit >= 0 ? 'rgba(18,128,92,0.12)' : 'rgba(217,45,45,0.12)',
          }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: profit >= 0 ? FIN_GREEN : FIN_RED }}>
                {profit >= 0 ? 'Profit' : 'Loss'}
              </div>
              <div style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontSize: 24, fontWeight: 700, color: profit >= 0 ? FIN_GREEN : FIN_RED, marginTop: 2 }}>
                {money(Math.abs(profit))}
              </div>
            </div>
            <div style={{ textAlign: 'right', fontSize: 12, fontWeight: 500, color: profit >= 0 ? FIN_GREEN : FIN_RED, opacity: 0.85 }}>
              Revenue {money(revenue)} − Cost {money(totalCost)}
              {data.margin_pct != null && <><br />{data.margin_pct}% margin</>}
            </div>
          </div>
        ) : (
          <div style={{ padding: '14px 20px', fontSize: 12, color: 'var(--fg-dim)' }}>
            Set a billing amount to see profit/loss.
          </div>
        )}
      </div>

      {/* Add entry modal */}
      <Modal
        open={addEntryOpen}
        onClose={submitting ? undefined : () => setAddEntryOpen(false)}
        title="Add entry"
      >
        <form onSubmit={handleAddEntry} style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div>
            <label style={labelStyle}>Entry type</label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[['cost', 'Cost', DollarSign], ['invoice', 'Invoice', FileText]].map(([k, label, Icon]) => (
                <button
                  key={k} type="button" onClick={() => setKind(k)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, height: 38, padding: '0 14px', borderRadius: 8,
                    border: `1px solid ${kind === k ? 'var(--fg)' : 'var(--border-strong)'}`,
                    background: kind === k ? 'var(--fg)' : 'var(--fin-card-bg)',
                    color: kind === k ? 'var(--bg-page)' : 'var(--fg)',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  <Icon size={14} /> {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label style={labelStyle} htmlFor="ae-desc">Description <span style={{ color: '#e05555' }}>*</span></label>
            <input
              id="ae-desc" ref={descInputRef} type="text" maxLength={300}
              placeholder={kind === 'invoice' ? 'Invoice #1001' : 'Software licences'}
              className="form-input" style={{ width: '100%' }}
              value={description} onChange={(e) => setDescription(e.target.value)}
            />
            <div style={{ fontSize: 11, color: 'var(--fg-mute)', textAlign: 'right', marginTop: 2 }}>
              {description.length}/300
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label style={labelStyle} htmlFor="ae-date">Date</label>
              <input
                id="ae-date" type="date" className="form-input" style={{ width: '100%' }}
                value={entryDate} onChange={(e) => setEntryDate(e.target.value)}
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="ae-amt">{kind === 'invoice' ? 'Invoice amount' : 'Cost'} <span style={{ color: '#e05555' }}>*</span></label>
              <input
                id="ae-amt" type="number" min="0" max={MAX_ENTRY_AMOUNT} step="0.01" placeholder="0.00"
                className="form-input" style={{ width: '100%' }}
                value={amount} onChange={(e) => setAmount(e.target.value)}
              />
            </div>
          </div>

          {formError && <div className="form-error" style={{ margin: 0 }}>{formError}</div>}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <button type="button" className="btn-secondary" onClick={() => setAddEntryOpen(false)} disabled={submitting}>
              Cancel
            </button>
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Adding…' : 'Add entry'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

// ── Main Modal ─────────────────────────────────────────────────────────────────

export default function ProjectViewModal({
  open, onClose, project, members, roles, areasByRole, onUpdate, actions,
}) {
  const { appId }      = useParams();
  const navigate       = useNavigate();
  const api            = useApi();
  const { me, myRole, organisation } = useWorkspace();

  // ── Tasks state ──────────────────────────────────────────────────────────────
  const [tasks,          setTasks]          = useState([]);
  const [tasksLoading,   setTasksLoading]   = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const [mailSettingsOpen, setMailSettingsOpen] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // ── Milestones state ─────────────────────────────────────────────────────────
  const [milestones, setMilestones] = useState([]);
  const [msLoading,  setMsLoading]  = useState(false);
  const [msError,    setMsError]    = useState(null);
  const [adding,     setAdding]     = useState(false);
  const [editingId,  setEditingId]  = useState(null);
  const [togglingId, setTogglingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [msFilter,   setMsFilter]   = useState('all');
  const [expandedMsId, setExpandedMsId] = useState(null);

  // ── Comments state ───────────────────────────────────────────────────────────
  const [comments,       setComments]       = useState([]);
  const [cmtLoading,     setCmtLoading]     = useState(false);
  const [cmtText,        setCmtText]        = useState('');
  const [cmtSubmitting,  setCmtSubmitting]  = useState(false);
  const [cmtError,       setCmtError]       = useState(null);
  const [deletingCmtId,  setDeletingCmtId]  = useState(null);
  const cmtInputRef = useRef(null);

  // ── Project completion ───────────────────────────────────────────────────────
  const [isCompleted,      setIsCompleted]      = useState(false);
  const [completing,       setCompleting]       = useState(false);
  const [completeError,    setCompleteError]    = useState(null);
  const [needFinalComment, setNeedFinalComment] = useState(false);

  // ── Top-level tab: Project Details | Financial Details (latter admin/owner only) ─
  const [mainTab, setMainTab] = useState('details');

  const isOwner = myRole === 'super_admin' || myRole === 'admin'
    || (me && project && me.id === project.owner_user_id);
  const isFinanceAdmin = isOwner;
  // An inactive (on-hold) project is read-only for everyone — the server
  // refuses every change too. Only "Set Active" on the Projects list works.
  const isInactive = !!project?.is_inactive;
  const canModify  = isOwner && !isInactive;

  // ── Loaders ──────────────────────────────────────────────────────────────────

  const loadTasks = useCallback(async () => {
    if (!project) return;
    setTasksLoading(true);
    try {
      const res = await api.listTasks(appId, { project_id: project.id });
      setTasks(Array.isArray(res) ? res : (res?.items || []));
    } catch { setTasks([]); }
    finally { setTasksLoading(false); }
  }, [api, appId, project]);

  const loadMilestones = useCallback(async () => {
    if (!project) return;
    setMsLoading(true); setMsError(null);
    try {
      const rows = await api.listMilestones(appId, project.id);
      setMilestones(rows || []);
    } catch (err) {
      setMsError(err.response?.data?.error || err.message || 'Failed to load milestones');
    } finally { setMsLoading(false); }
  }, [api, appId, project]);

  const loadComments = useCallback(async () => {
    if (!project) return;
    setCmtLoading(true);
    try {
      const rows = await api.listProjectComments(appId, project.id);
      setComments(rows || []);
    } catch { setComments([]); }
    finally { setCmtLoading(false); }
  }, [api, appId, project]);

  useEffect(() => {
    if (open && project) {
      loadTasks();
      loadMilestones();
      loadComments();
      setAdding(false);
      setEditingId(null);
      setMsFilter('all');
      setExpandedMsId(null);
      setCmtText('');
      setCmtError(null);
      setMainTab('details');
      setIsCompleted(!!project.is_completed);
      setCompleteError(null);
      setNeedFinalComment(false);
    }
  }, [open, project]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Comment handlers ─────────────────────────────────────────────────────────

  async function handleAddComment(e) {
    e.preventDefault();
    if (!cmtText.trim()) return;
    setCmtSubmitting(true);
    setCmtError(null);
    try {
      await api.createProjectComment(appId, project.id, { content: cmtText.trim() });
      setCmtText('');
      setNeedFinalComment(false);
      await loadComments();
    } catch (err) {
      setCmtError(err.response?.data?.error || err.message || 'Failed to post comment');
    } finally {
      setCmtSubmitting(false);
    }
  }

  async function handleDeleteComment(cid) {
    setDeletingCmtId(cid);
    try {
      await api.deleteProjectComment(appId, project.id, cid);
      setComments((prev) => prev.filter((c) => c.id !== cid));
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally {
      setDeletingCmtId(null);
    }
  }

  // ── Completion handlers ──────────────────────────────────────────────────────

  // Completing needs a final comment: one posted after the last task /
  // milestone was completed (the server enforces the same rule).
  async function handleCompleteProject() {
    setCompleteError(null);
    if (!hasFinalComment) {
      setNeedFinalComment(true);
      cmtInputRef.current?.focus();
      return;
    }
    setCompleting(true);
    try {
      await api.updateProject(appId, project.id, { is_completed: 1 });
      setIsCompleted(true);
      onUpdate?.();
    } catch (err) {
      if (err.response?.data?.code === 'FINAL_COMMENT_REQUIRED') {
        setNeedFinalComment(true);
        cmtInputRef.current?.focus();
      } else {
        setCompleteError(err.response?.data?.error || err.message || 'Failed to complete project');
      }
    } finally {
      setCompleting(false);
    }
  }

  async function handleReopenProject() {
    setCompleteError(null);
    setCompleting(true);
    try {
      await api.updateProject(appId, project.id, { is_completed: 0 });
      setIsCompleted(false);
      onUpdate?.();
    } catch (err) {
      setCompleteError(err.response?.data?.error || err.message || 'Failed to reopen project');
    } finally {
      setCompleting(false);
    }
  }

  // ── Milestone handlers ───────────────────────────────────────────────────────

  async function handleAddMs(body) {
    await api.createMilestone(appId, project.id, body);
    setAdding(false);
    await loadMilestones();
    onUpdate?.();
  }
  async function handleEditMs(mid, body) {
    await api.updateMilestone(appId, project.id, mid, body);
    setEditingId(null);
    await loadMilestones();
    onUpdate?.();
  }
  async function handleToggleMs(ms) {
    setTogglingId(ms.id);
    try {
      await api.updateMilestone(appId, project.id, ms.id, { is_completed: ms.is_completed ? 0 : 1 });
      await loadMilestones();
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally { setTogglingId(null); }
  }
  async function handleDeleteMs(mid) {
    setDeletingId(mid);
    try {
      await api.deleteMilestone(appId, project.id, mid);
      await loadMilestones();
      onUpdate?.();
    } catch (err) {
      alert(err.response?.data?.error || err.message);
    } finally { setDeletingId(null); }
  }

  if (!project) return null;

  // ── Derived values ───────────────────────────────────────────────────────────

  const statusMeta  = PROJECT_STATUS_META[project.computed_status] || { label: project.computed_status, color: '#06b6d4' };
  const msTotal     = milestones.length;
  // When the project's work finished: the latest completion time across done
  // tasks and completed milestones. The final comment must come after it.
  const workFinishedAt = Math.max(
    0,
    ...tasks.filter((t) => t.status === 'done' && t.completed_at).map((t) => new Date(t.completed_at).getTime()),
    ...milestones.filter((m) => m.is_completed && m.completed_at).map((m) => new Date(m.completed_at).getTime()),
  );
  const hasFinalComment = comments.some((c) => new Date(c.created_at).getTime() > workFinishedAt);

  const msCompleted = milestones.filter((m) =>  m.is_completed).length;
  const msOverdue   = milestones.filter((m) => !m.is_completed && m.end_date && m.end_date < TODAY).length;
  const msPct       = msTotal === 0 ? 0 : Math.round((msCompleted / msTotal) * 100);

  const MS_FILTERS = [
    { value: 'all',       label: 'All',       count: msTotal },
    { value: 'pending',   label: 'Pending',   count: msTotal - msCompleted },
    { value: 'completed', label: 'Completed', count: msCompleted },
    { value: 'overdue',   label: 'Overdue',   count: msOverdue },
  ];
  const visibleMs = milestones.filter((ms) => {
    if (msFilter === 'completed') return  !!ms.is_completed;
    if (msFilter === 'pending')   return  !ms.is_completed;
    if (msFilter === 'overdue')   return  !ms.is_completed && ms.end_date && ms.end_date < TODAY;
    return true;
  });

  // Member lookup by user id
  const membersMap = {};
  (members || []).forEach((m) => { membersMap[m.user_id] = m; });

  // Tasks grouped by milestone id
  const tasksByMilestone = {};
  tasks.forEach((t) => {
    if (t.milestone_id == null) return;
    (tasksByMilestone[t.milestone_id] ||= []).push(t);
  });

  // Admin/owner-only "Download PDF" — combines Project Details and (when
  // permitted) Financial Details into one branded report matching the
  // approved Corro-style reference design (dark header, KPI cards, a
  // financial grid with type badges, and a profit donut).
  async function handleDownloadPdf() {
    setDownloadingPdf(true);
    try {
      const reportProject = {
        name: project.name,
        statusLabel: statusMeta.label,
        client: project.client_name || null,
        owner: project.owner_name || null,
        startDate: project.start_date || null,
        endDate: project.end_date || null,
        members: (project.members || []).map((m) => m.name || m.email).join(', ') || null,
      };

      const reportMilestones = milestones.map((m) => ({
        title: m.title,
        subtitle: [m.start_date, m.end_date].filter(Boolean).join(' - '),
        statusLabel: m.is_completed ? 'Completed' : 'Pending',
        done: !!m.is_completed,
      }));

      const reportTasks = tasks.map((t) => {
        const member = t.assigned_to_user_id ? membersMap[t.assigned_to_user_id] : null;
        const memberName = member ? (member.name || [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email) : 'Unassigned';
        return {
          title: t.title,
          subtitle: `${memberName}${t.total_minutes ? ` • ${formatHoursMinutes(t.total_minutes)} logged` : ''}`,
          statusLabel: TASK_STATUS_META[t.status]?.label || t.status,
          done: t.status === 'done',
        };
      });

      let reportFinancials = null;
      if (isFinanceAdmin) {
        const fin = await api.getProjectFinancials(appId, project.id);
        const revenue       = Number(fin.revenue) || 0;
        const invoicedTotal = Number(fin.invoiced_total) || 0;
        const totalCost     = Number(fin.total_cost) || 0;
        const profit        = Number(fin.profit) || 0;
        const profitToDate  = invoicedTotal - totalCost;
        const billedPct     = revenue > 0 ? (invoicedTotal / revenue) * 100 : 0;
        const costEntries    = (fin.costs || []).filter((c) => c.kind !== 'invoice');
        const invoiceEntries = (fin.costs || []).filter((c) => c.kind === 'invoice');

        const rows = [{
          no: 1, item: 'Project value', type: 'Revenue',
          date: project.start_date ? ledgerDate(dateOnly(project.start_date)) : '—',
          estimate: revenue ? money(revenue) : '—', actual: '—', cost: '—',
        }];
        let n = 2;
        if (Number(fin.actual_labor_cost) > 0) {
          rows.push({
            no: n++, item: `Hours worked${fin.actual_hours ? ` (${hoursLabel(fin.actual_hours)})` : ''}`, type: 'Auto',
            date: ledgerDate(TODAY), estimate: '—', actual: '—', cost: money(fin.actual_labor_cost),
          });
        }
        costEntries.forEach((c) => rows.push({
          no: n++, item: c.description, type: 'Cost',
          date: ledgerDate(dateOnly(c.entry_date)), estimate: '—', actual: '—', cost: money(c.amount),
        }));
        invoiceEntries.forEach((c) => rows.push({
          no: n++, item: c.description, type: 'Invoice',
          date: ledgerDate(dateOnly(c.entry_date)), estimate: '—', actual: money(c.amount), cost: '—',
        }));

        reportFinancials = {
          cards: [
            { label: 'Project Value', value: moneyShort(revenue), sub: fin.billing_type === 'per_hour' ? 'Per-hour estimate' : 'Fixed-rate estimate' },
            { label: 'Invoiced', value: moneyShort(invoicedTotal), progressPct: billedPct, sub: `${Math.round(billedPct)}% billed` },
            { label: 'Total Cost', value: moneyShort(totalCost), color: [230, 57, 70], sub: `Labor ${moneyShort(fin.actual_labor_cost)} • Manual ${moneyShort(fin.other_costs_total)}` },
            { label: 'Projected Profit', value: moneyShort(profit), color: profit >= 0 ? [0, 163, 137] : [230, 57, 70], sub: fin.margin_pct != null ? `${fin.margin_pct}% margin` : '—' },
            { label: 'Profit To Date', value: moneyShort(profitToDate), color: profitToDate >= 0 ? [0, 163, 137] : [230, 57, 70], sub: 'Invoiced minus cost' },
          ],
          rows,
          total: { estimate: money(revenue), actual: money(invoicedTotal), cost: money(totalCost) },
          marginPct: fin.margin_pct,
          profit,
          profitLabel: money(Math.abs(profit)),
          revenueLabel: money(revenue),
          costLabel: money(totalCost),
        };
      }

      await buildProjectReportPdf({
        orgName: organisation?.name || '',
        logoSrc: organisation?.logo_data || fullLogoUrl(organisation?.logo_url),
        project: reportProject,
        milestones: reportMilestones,
        tasks: reportTasks,
        financials: reportFinancials,
      });
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Failed to generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="3xl"
        headerExtra={(
          <SettingsMenu
            actions={actions}
            onSelectMailControl={() => setMailSettingsOpen(true)}
            onSelectDownload={handleDownloadPdf}
            showDownload={isFinanceAdmin}
            downloadBusy={downloadingPdf}
          />
        )}
        title={
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span>{project.name}</span>
            <span style={{
              fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 99,
              background: `${statusMeta.color}18`, color: statusMeta.color,
              border: `1px solid ${statusMeta.color}35`,
            }}>
              {statusMeta.label}
            </span>
            {project.completion_pct !== undefined && (
              <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--text-muted)' }}>
                {project.completion_pct}% complete · {project.completed_milestones ?? 0}/{project.total_milestones ?? 0} milestone{project.total_milestones !== 1 ? 's' : ''}
              </span>
            )}
          </span>
        }
      >
        {isInactive && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, padding: '10px 14px',
            borderRadius: 10, fontSize: 13, lineHeight: 1.5,
            background: 'rgba(217,119,6,0.10)', border: '1px solid rgba(217,119,6,0.35)', color: '#d97706',
          }}>
            <span style={{ fontSize: 15 }}>⏸️</span>
            <span>
              <strong>This project is inactive (on hold).</strong> It&apos;s view-only — milestones, tasks, comments,
              costs and mail settings can&apos;t be changed.{isOwner ? ' Use "Set Active" on the Projects list to make changes again.' : ''}
            </span>
          </div>
        )}

        {isFinanceAdmin && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
            <button
              onClick={() => setMainTab('details')}
              style={{
                padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${mainTab === 'details' ? '#06b6d4' : 'var(--border)'}`,
                background: mainTab === 'details' ? 'rgba(6,182,212,0.12)' : 'transparent',
                color: mainTab === 'details' ? '#06b6d4' : 'var(--fg-dim)',
              }}
            >
              Project Details
            </button>
            <button
              onClick={() => setMainTab('financials')}
              style={{
                padding: '6px 16px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                border: `1px solid ${mainTab === 'financials' ? '#06b6d4' : 'var(--border)'}`,
                background: mainTab === 'financials' ? 'rgba(6,182,212,0.12)' : 'transparent',
                color: mainTab === 'financials' ? '#06b6d4' : 'var(--fg-dim)',
              }}
            >
              Financial Details
            </button>
          </div>
        )}

        {mainTab === 'financials' && isFinanceAdmin ? (
          <FinancialsPanel appId={appId} api={api} projectId={project.id} startDate={project.start_date} readOnly={isInactive} />
        ) : (
        <div className="tdv2-body">
          <div className="tdv2-main">

            {/* ── 1. Project Details ───────────────────────────────────────── */}
            <div className="tdv2-meta-card">
              {project.client_name && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Client</span>
                  <span className="tdv2-field-static">{project.client_name}</span>
                </div>
              )}
              {project.owner_name && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Owner</span>
                  <span className="tdv2-field-static">{project.owner_name}</span>
                </div>
              )}
              {project.creator_name && project.creator_name !== project.owner_name && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Created By</span>
                  <span className="tdv2-field-static">{project.creator_name}</span>
                </div>
              )}
              {project.start_date && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">Start</span>
                  <span className="tdv2-field-static">{project.start_date}</span>
                </div>
              )}
              {project.end_date && (
                <div className="tdv2-field">
                  <span className="tdv2-field-label">End</span>
                  <span className="tdv2-field-static">{project.end_date}</span>
                </div>
              )}
              {project.client_details && (
                <div className="tdv2-field" style={{ gridColumn: '1 / -1' }}>
                  <span className="tdv2-field-label">Details</span>
                  <span className="tdv2-field-static">{project.client_details}</span>
                </div>
              )}
              {project.members?.length > 0 && (
                <div className="tdv2-field" style={{ gridColumn: '1 / -1' }}>
                  <span className="tdv2-field-label">Members</span>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 3 }}>
                    {project.members.map((m) => (
                      <span key={m.user_id} style={{
                        fontSize: 12, fontWeight: 500, padding: '3px 10px', borderRadius: 99,
                        background: 'var(--bg-muted)', border: '1px solid var(--border)', color: 'var(--text-secondary)',
                      }}>
                        {m.name || m.email}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Remarks */}
            {project.remarks && (
              <div className="tdv2-section-card">
                <span className="tdv2-section-label">Remarks</span>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{project.remarks}</div>
              </div>
            )}

          {/* ── 2. Milestones (with tasks nested inside each) ───────────────── */}
          <div className="tdv2-section-card">
            <SectionHeader
              title={`Milestones${msTotal > 0 ? ` · ${msCompleted}/${msTotal} done` : ''}`}
              action={
                <div style={{ display: 'flex', gap: 8 }}>
                  <button
                    className="btn-primary"
                    style={{ padding: '4px 14px', fontSize: 12, opacity: isInactive ? 0.5 : 1, cursor: isInactive ? 'not-allowed' : 'pointer' }}
                    onClick={() => setCreateTaskOpen(true)}
                    disabled={isInactive}
                    title={isInactive ? 'Project is inactive. Set it active to add tasks.' : undefined}
                  >
                    + New Task
                  </button>
                  {canModify && !adding && !msLoading && (
                    <button className="btn-secondary" style={{ padding: '4px 12px', fontSize: 12 }} onClick={() => { setAdding(true); setEditingId(null); }}>
                      + Add Milestone
                    </button>
                  )}
                </div>
              }
            />

            {msError   && <div className="banner banner-error">{msError}</div>}
            {(msLoading || tasksLoading) && <div className="tl-loading"><span className="tl-spinner" /> Loading milestones…</div>}

            {/* Progress bar */}
            {!msLoading && msTotal > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <div style={{ flex: 1, background: 'var(--bg-4)', borderRadius: 99, height: 5, overflow: 'hidden' }}>
                  <div style={{ width: `${msPct}%`, height: '100%', background: 'linear-gradient(90deg,#06b6d499,#06b6d4)', borderRadius: 99, transition: 'width .4s' }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#06b6d4', minWidth: 52 }}>{msPct}% done</span>
              </div>
            )}

            {/* Filter tabs */}
            {!msLoading && msTotal > 0 && (
              <div style={{
                display: 'flex', gap: 5, flexWrap: 'wrap', marginBottom: 10,
                padding: 5, borderRadius: 10,
                background: 'var(--bg-3)', border: '1px solid var(--border)',
              }}>
                {MS_FILTERS.map((f) => (
                  <button
                    key={f.value} onClick={() => setMsFilter(f.value)}
                    style={{
                      padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, cursor: 'pointer',
                      border: `1px solid ${msFilter === f.value ? '#06b6d4' : 'transparent'}`,
                      background: msFilter === f.value ? 'rgba(6,182,212,0.15)' : 'var(--bg-4)',
                      color: msFilter === f.value ? '#06b6d4' : 'var(--fg-dim)', transition: 'all .15s',
                    }}
                  >
                    {f.label}
                    <span style={{
                      marginLeft: 4, fontSize: 9, fontWeight: 700,
                      background: msFilter === f.value ? '#06b6d4' : 'rgba(255,255,255,0.1)',
                      color: msFilter === f.value ? '#000' : 'var(--fg-dim)',
                      padding: '1px 5px', borderRadius: 99,
                    }}>
                      {f.count}
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* Milestone list */}
            {!msLoading && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {msTotal === 0 && !adding && (
                  <div style={{ textAlign: 'center', padding: '18px 0', color: 'var(--fg-dim)', fontSize: 13 }}>
                    No milestones yet.{canModify ? ' Add one above.' : ''}
                  </div>
                )}
                {visibleMs.map((ms) => {
                  const isCompleted   = !!ms.is_completed;
                  const isOverdue     = !isCompleted && ms.end_date && ms.end_date < TODAY;
                  const isToggling    = togglingId === ms.id;
                  const isDeleting    = deletingId === ms.id;
                  const isEditingThis = editingId  === ms.id;
                  const msTasks       = tasksByMilestone[ms.id] || [];
                  const isExpanded    = expandedMsId === ms.id;

                  return (
                    <div key={ms.id}>
                      {isEditingThis && canModify ? (
                        <MilestoneForm
                          initial={ms}
                          onSave={(body) => handleEditMs(ms.id, body)}
                          onCancel={() => setEditingId(null)}
                        />
                      ) : (
                        <div
                          onClick={() => setExpandedMsId(isExpanded ? null : ms.id)}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 10, padding: '9px 13px',
                            background: isCompleted ? 'rgba(74,210,149,0.05)' : isOverdue ? 'rgba(248,113,113,0.04)' : 'rgba(255,255,255,0.03)',
                            border: `1px solid ${isCompleted ? 'rgba(74,210,149,0.2)' : isOverdue ? 'rgba(248,113,113,0.18)' : 'rgba(255,255,255,0.07)'}`,
                            borderRadius: isExpanded ? '8px 8px 0 0' : 8,
                            cursor: 'pointer',
                          }}
                        >
                          {/* Toggle — owner/admin only */}
                          <div
                            title={canModify ? (isCompleted ? 'Mark incomplete' : 'Mark complete') : undefined}
                            style={{
                              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
                              border: `2px solid ${isCompleted ? '#4ad295' : 'var(--border-strong)'}`,
                              background: isCompleted ? '#4ad295' : 'transparent',
                              cursor: canModify && !isToggling ? 'pointer' : 'default',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              opacity: isToggling ? 0.5 : 1, transition: 'all .2s',
                            }}
                            onClick={(e) => { e.stopPropagation(); if (canModify && !isToggling) handleToggleMs(ms); }}
                          >
                            {isCompleted && (
                              <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
                                <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                              </svg>
                            )}
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', textDecoration: isCompleted ? 'line-through' : 'none', opacity: isCompleted ? 0.55 : 1 }}>
                              {ms.title}
                            </div>
                            {(ms.start_date || ms.end_date) && (
                              <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 2 }}>
                                {ms.start_date && <span>Start: {ms.start_date}</span>}
                                {ms.start_date && ms.end_date && ' · '}
                                {ms.end_date   && <span>End: {ms.end_date}</span>}
                              </div>
                            )}
                          </div>

                          <span style={{ fontSize: 11, color: 'var(--fg-dim)', flexShrink: 0 }}>
                            {msTasks.length} task{msTasks.length !== 1 ? 's' : ''}
                          </span>

                          {isCompleted && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'rgba(74,210,149,0.12)', color: '#4ad295', border: '1px solid rgba(74,210,149,0.25)', flexShrink: 0 }}>
                              Done
                            </span>
                          )}
                          {isOverdue && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 99, background: 'rgba(248,113,113,0.12)', color: '#f87171', border: '1px solid rgba(248,113,113,0.25)', flexShrink: 0 }}>
                              Overdue
                            </span>
                          )}

                          {canModify && (
                            <div style={{ display: 'flex', gap: 5, flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                              <button
                                className="btn-secondary"
                                style={{ padding: '2px 9px', fontSize: 11 }}
                                onClick={() => { setEditingId(ms.id); setAdding(false); }}
                              >
                                Edit
                              </button>
                              <button
                                className="btn-secondary"
                                style={{ padding: '2px 9px', fontSize: 11, color: '#f87171', borderColor: 'rgba(248,113,113,0.3)', opacity: isDeleting ? 0.5 : 1 }}
                                onClick={() => handleDeleteMs(ms.id)}
                                disabled={isDeleting}
                              >
                                {isDeleting ? '…' : 'Delete'}
                              </button>
                            </div>
                          )}

                          <span style={{ fontSize: 13, color: 'var(--fg-mute)', flexShrink: 0, transform: isExpanded ? 'rotate(90deg)' : 'none', transition: 'transform .15s' }}>
                            ›
                          </span>
                        </div>
                      )}

                      {/* Assigned tasks for this milestone */}
                      {isExpanded && !isEditingThis && (
                        <div style={{
                          border: '1px solid rgba(255,255,255,0.07)', borderTop: 'none',
                          borderRadius: '0 0 8px 8px', padding: '10px 13px',
                          background: 'rgba(255,255,255,0.015)',
                        }}>
                          <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--fg-mute)', marginBottom: 8 }}>
                            Tasks
                          </div>
                          {msTasks.length === 0 && (
                            <div style={{ fontSize: 12, color: 'var(--fg-dim)', textAlign: 'center', padding: '4px 0' }}>
                              No tasks assigned to this milestone.
                            </div>
                          )}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                            {msTasks.map((task, i) => {
                              const tm     = TASK_STATUS_META[task.status] || { label: task.status, color: '#06b6d4', bg: 'rgba(6,182,212,0.12)', border: '#06b6d4' };
                              const member = task.assigned_to_user_id ? membersMap[task.assigned_to_user_id] : null;
                              const memberName = member ? (member.name || [member.first_name, member.last_name].filter(Boolean).join(' ') || member.email) : 'Unassigned';
                              const loggedMins = task.total_minutes || 0;

                              return (
                                <div
                                  key={task.id}
                                  onClick={() => {
                                    onClose();
                                    navigate(`/apps/${appId}/tasks/${task.id}`);
                                  }}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 10,
                                    padding: '8px 10px',
                                    background: 'var(--bg-3)', border: '1px solid var(--border)',
                                    borderRadius: 8, cursor: 'pointer',
                                    transition: 'background .15s, border-color .15s',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-4)'; e.currentTarget.style.borderColor = 'var(--border-strong)'; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--bg-3)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
                                >
                                  <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-mute)', flexShrink: 0, minWidth: 16 }}>
                                    {i + 1}
                                  </span>
                                  {task.task_number && (
                                    <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--fg-mute)', minWidth: 68, flexShrink: 0 }}>
                                      {task.task_number}
                                    </span>
                                  )}
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                      {task.title}
                                    </div>
                                    <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 1 }}>
                                      👤 {memberName}
                                    </div>
                                  </div>
                                  <span style={{
                                    fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 99, flexShrink: 0,
                                    background: tm.bg, color: tm.color, border: `1px solid ${tm.border}30`,
                                  }}>
                                    {tm.label}
                                  </span>
                                  {loggedMins > 0 && (
                                    <span style={{ fontSize: 11, color: 'var(--fg-dim)', flexShrink: 0 }}>
                                      ⏱ {formatHoursMinutes(loggedMins)} logged
                                    </span>
                                  )}
                                  <span style={{ fontSize: 13, color: 'var(--fg-mute)', flexShrink: 0 }}>›</span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {adding && canModify && (
                  <MilestoneForm onSave={handleAddMs} onCancel={() => setAdding(false)} />
                )}
              </div>
            )}
          </div>

          </div>

          {/* ── Sidebar: Comments ────────────────────────────────────────────── */}
          <div className="tdv2-sidebar">
          <div className="tdv2-panel">
            <div className="tdv2-panel-head">
              <span className="tdv2-panel-title">Comments</span>
              {comments.length > 0 && <span className="tdv2-panel-total">{comments.length}</span>}
            </div>

            {/* Comment list */}
            {cmtLoading ? (
              <div className="tl-loading"><span className="tl-spinner" /> Loading comments…</div>
            ) : comments.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--fg-dim)', fontSize: 13 }}>
                No comments yet. Be the first to comment.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
                {comments.map((c) => {
                  const name   = [c.first_name, c.last_name].filter(Boolean).join(' ') || c.email;
                  const initials = name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
                  const isMe   = me && c.user_id === me.id;
                  const canDel = (isMe || isOwner) && !isInactive;
                  const isDel  = deletingCmtId === c.id;
                  const ts     = new Date(c.created_at);
                  const dateStr = ts.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                  const timeStr = ts.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

                  return (
                    <div key={c.id} style={{
                      display: 'flex', gap: 10, alignItems: 'flex-start',
                      padding: '10px 12px',
                      background: isMe ? 'rgba(6,182,212,0.04)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${isMe ? 'rgba(6,182,212,0.15)' : 'rgba(255,255,255,0.06)'}`,
                      borderRadius: 9,
                    }}>
                      {/* Avatar */}
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                        background: isMe ? 'rgba(6,182,212,0.2)' : 'rgba(255,255,255,0.08)',
                        border: `1px solid ${isMe ? 'rgba(6,182,212,0.35)' : 'rgba(255,255,255,0.12)'}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 11, fontWeight: 700, color: isMe ? '#06b6d4' : 'var(--fg-dim)',
                      }}>
                        {initials}
                      </div>
                      {/* Body */}
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg)' }}>{name}</span>
                          {isMe && (
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 99, background: 'rgba(6,182,212,0.12)', color: '#06b6d4', border: '1px solid rgba(6,182,212,0.25)' }}>
                              You
                            </span>
                          )}
                          <span style={{ fontSize: 11, color: 'var(--fg-mute)', marginLeft: 'auto' }}>{dateStr} · {timeStr}</span>
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--fg-dim)', lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {c.content}
                        </div>
                      </div>
                      {/* Delete */}
                      {canDel && (
                        <button
                          onClick={() => handleDeleteComment(c.id)}
                          disabled={isDel}
                          title="Delete comment"
                          style={{
                            background: 'none', border: 'none', cursor: isDel ? 'wait' : 'pointer',
                            color: 'var(--fg-mute)', fontSize: 14, padding: '2px 4px', flexShrink: 0,
                            opacity: isDel ? 0.4 : 0.6, lineHeight: 1,
                          }}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* New comment form — hidden while the project is inactive */}
            {isInactive ? (
              <div style={{ fontSize: 12, color: 'var(--fg-dim)', fontStyle: 'italic', textAlign: 'center', padding: '6px 0' }}>
                Comments are closed while the project is inactive.
              </div>
            ) : (<>
            <form onSubmit={handleAddComment} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <textarea
                ref={cmtInputRef}
                className="form-input"
                rows={2}
                placeholder={needFinalComment ? 'Write a final comment…' : 'Write a comment…'}
                value={cmtText}
                onChange={(e) => setCmtText(e.target.value)}
                maxLength={2000}
                disabled={cmtSubmitting}
                style={{ flex: 1, margin: 0, resize: 'vertical', minHeight: 60 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleAddComment(e);
                }}
              />
              <button
                type="submit"
                className="btn-primary"
                disabled={cmtSubmitting || !cmtText.trim()}
                style={{ padding: '8px 16px', fontSize: 13, flexShrink: 0 }}
              >
                {cmtSubmitting ? 'Posting…' : 'Post'}
              </button>
            </form>
            {cmtError && <div className="form-error" style={{ marginTop: 6 }}>{cmtError}</div>}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
              <span>Ctrl+Enter to post</span>
              <span>{cmtText.length}/2000</span>
            </div>
            </>)}
            {/* ── Project completion — inside the comment box ─────────────── */}
            {canModify && (
              <div style={{ marginTop: 12 }}>
                {needFinalComment && !isCompleted && (
                  <div className="banner banner-error" style={{ marginBottom: 8, fontSize: 12 }}>
                    Add a final comment before completing the project. Post it above, then click Complete Project again.
                  </div>
                )}
                {!needFinalComment && !isCompleted && !hasFinalComment && !cmtLoading && !tasksLoading && !msLoading && (
                  <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginBottom: 6, textAlign: 'center' }}>
                    A final comment posted after the last task / milestone was completed is required.
                  </div>
                )}
                {completeError && (
                  <div className="form-error" style={{ marginBottom: 8 }}>{completeError}</div>
                )}
                {isCompleted ? (
                  <button
                    className="btn-secondary"
                    onClick={handleReopenProject}
                    disabled={completing}
                    style={{ width: '100%', padding: '10px 16px', fontSize: 13 }}
                  >
                    {completing ? 'Reopening…' : '↩️ Reopen Project'}
                  </button>
                ) : (
                  <button
                    className="btn-primary"
                    onClick={handleCompleteProject}
                    disabled={completing || cmtLoading}
                    style={{ width: '100%', padding: '10px 16px', fontSize: 13, background: '#16a34a', borderColor: 'transparent' }}
                  >
                    {completing ? 'Completing…' : '✓ Complete Project'}
                  </button>
                )}
              </div>
            )}
          </div>

          </div>

        </div>
        )}
      </Modal>

      {/* Create Task modal — rendered outside so it layers above ProjectViewModal */}
      <CreateTaskModal
        open={createTaskOpen}
        onClose={() => setCreateTaskOpen(false)}
        members={members}
        roles={roles || []}
        areasByRole={areasByRole || new Map()}
        defaultProjectId={project?.id}
        onCreated={async () => { setCreateTaskOpen(false); await loadTasks(); onUpdate?.(); }}
      />

      <ProjectMailSettingsModal
        open={mailSettingsOpen}
        onClose={() => setMailSettingsOpen(false)}
        appId={appId}
        project={project}
        canEdit={canModify}
      />
    </>
  );
}
